import { CopyOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { filter, forEach, isEmpty, map } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import {
  ANNUALLY_LITE,
  DATE_FORMATS,
  LIMIT,
  PAYOUT_LINK_STATUS,
  PLAN_NAMES,
  PLAN_NAMES_OPTIONS,
  PLAN_TYPES,
  SORT,
  TABLE_FILTER_KEYS,
} from '../../../../../common/constants';
import {
  getPlayPlanLabel,
  handleOpenUserDrawer,
  isSalesUser,
  toDayjsRange,
} from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import useRetainedTableState from '../../../../../hooks/useRetainedTableFilterState';
import { GET_PAYOUT_LINKS } from '../graphql/Queries';

const { RangePicker } = DatePicker;
const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialFilter = {
  search: '',
  limit: 10,
  skip: 0,
};

const initialSort = {
  field: 'createdAt',
  order: 'DESC',
};

const PAYMENT_STATUS_OPTIONS = [
  { label: 'Paid', key: 'PAID' },
  { label: 'Pending', key: 'PENDING' },
  { label: 'Expired', key: 'EXPIRED' },
];

const UserPayoutLinksTable = ({ exportFilter, setExportFilter }) => {
  const {
    dispatch,
    state: {
      currentUser,
      commonPermissions: { isUserPermission },
    },
  } = useContext(AppContext);

  const history = useHistory();

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState('');
  const [isEmptyList, setIsEmptyList] = useState(false);

  // Filter state management
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState({});
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState({});
  const [finalFilterTags, setFinalFilterTags] = useState({});
  const [paymentLinksData, setPaymentLinksData] = useState([]);

  const mapApiResponseToTableData = (apiData) => {
    if (!apiData) return [];
    return apiData?.map((offer) => {
      const isExpired =
        dayjs(offer?.expiryDate).isBefore(dayjs()) || !offer?.status;
      return {
        id: offer?.id,
        subscriptionPlanId: offer?.subscriptionPlan?.id,
        title: `${
          offer?.subscriptionPlan?.name === PLAN_NAMES.PLAY.value
            ? getPlayPlanLabel(offer?.subscriptionPlan)
            : offer?.subscriptionPlan?.name
        }-${PLAN_TYPES?.[offer?.subscriptionPlan?.type]?.key}  ${
          offer?.subscriptionPlan?.slug === ANNUALLY_LITE ? ' (Lite)' : ''
        } ${
          offer?.subscriptionPlan?.subscriptionFeatures?.planVersion
            ? `(${offer?.subscriptionPlan?.subscriptionFeatures?.planVersion})`
            : ''
        }`,
        actualPrice: offer?.actualPrice,
        sellingPrice: offer?.sellingPrice,
        paymentStatus: offer?.status,
        createdBy: offer?.creator,
        createdAt: offer?.createdAt,
        expiredAt: offer?.expiredAt,
        planExpiryDate: offer?.metaData?.planExpiryDate,
        paymentLink: offer?.paymentLink,
        user: offer?.user,
        phoneNo: offer?.user?.contactNo,
        email: offer?.user?.email,
      };
    });
  };
  const [initializedFromRetained, setInitializedFromRetained] = useState(false);
  const {
    hydrated,
    retainedState,
    saveRetainedFilterState,
  } = useRetainedTableState();
  const finalWhereFiltersRef = useRef(null);
  const sortedInfoRef = useRef(null);
  const paginationPropRef = useRef(null);
  const adminSearchTermRef = useRef(null);

  useEffect(() => {
    if (!hydrated) return;
    if (initializedFromRetained) return;

    const retainedFiters =
      retainedState?.[TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS];

    if (!retainedFiters) {
      setInitializedFromRetained(true);
      return;
    }
    const baseFilters = retainedFiters?.filters || {};
    const normalizedFilters = {
      ...baseFilters,
      createdAt: toDayjsRange(baseFilters?.createdAt),
    };
    setFinalWhereFilters(normalizedFilters);
    finalWhereFiltersRef.current = normalizedFilters;
    setFilterTags(retainedFiters?.filterTags || {});
    setFinalFilterTags(retainedFiters?.filterTags || {});
    setSortedInfo(retainedFiters?.sort);
    sortedInfoRef.current = retainedFiters?.sort;

    setPaginationProp(retainedFiters?.paginationProp);
    paginationPropRef.current = retainedFiters?.paginationProp;

    setSearchTerm(retainedFiters?.searchTerm);
    adminSearchTermRef.current = retainedFiters?.searchTerm;
    setQuery(retainedFiters?.searchTerm);
    setInitializedFromRetained(true);
  }, [hydrated, initializedFromRetained, retainedState]);

  const [paymentLinks, { loading, data }] = useLazyQuery(GET_PAYOUT_LINKS, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      if (
        response?.paymentLinks?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyList(true);
      } else {
        setIsEmptyList(false);
      }
      const tableData = mapApiResponseToTableData(
        response?.paymentLinks?.data || [],
      );
      setPaymentLinksData(tableData);
      const pagination = {
        ...paginationProp,
        total: response?.paymentLinks?.count,
      };
      setPaginationProp(pagination);
      saveRetainedFilterState(TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS, {
        paginationProp: pagination,
      });
    },
    onError: () => {},
  });

  useEffect(() => {
    paymentLinks({
      variables: {
        filter: {
          ...initialFilter,
          skip:
            (paginationPropRef?.current?.current - 1) *
              (paginationPropRef?.current?.pageSize || 0) || 0,
          limit: paginationProp?.pageSize || LIMIT,
          search: searchTerm,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : undefined,
          subscriptionPlanId:
            finalWhereFiltersRef?.current?.subscriptionPlanId?.length > 0
              ? finalWhereFiltersRef?.current?.subscriptionPlanId
              : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialSort,
      },
    });
  }, [hydrated, initializedFromRetained]);

  const handleCopyLink = (link) => {
    // eslint-disable-next-line no-undef
    navigator.clipboard.writeText(link);
    message.success('Link copied to clipboard');
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    saveRetainedFilterState(TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS, {
      paginationProp: { ...paginationProp, current: 1, skip: 0 },
      searchTerm: trimValue,
    });
    paymentLinks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          subscriptionPlanId:
            finalWhereFilters?.subscriptionPlanId?.length > 0
              ? finalWhereFilters?.subscriptionPlanId
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
    if (exportFilter) {
      setExportFilter({ ...exportFilter, search: trimValue });
    }
  };

  const getFilterData = (confirm) => {
    paymentLinks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          skip: 0,
          search: searchTerm,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          subscriptionPlanId:
            whereFilter?.subscriptionPlanId?.length > 0
              ? whereFilter?.subscriptionPlanId
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    if (exportFilter) {
      setExportFilter({
        ...exportFilter,
        ...finalWhereFilters,
        ...whereFilter,
      });
    }
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    saveRetainedFilterState(TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS, {
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
      filters: { ...finalWhereFilters, ...whereFilter },
      filterTags,
    });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    if (exportFilter) {
      setExportFilter({
        ...exportFilter,
        ...finalWhereFilters,
        ...whereFiltersCopy,
      });
    }
    paymentLinks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          skip: 0,
          search: searchTerm,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          subscriptionPlanId:
            whereFiltersCopy?.subscriptionPlanId?.length > 0
              ? whereFiltersCopy?.subscriptionPlanId
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    saveRetainedFilterState(TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS, {
      filters: { ...finalWhereFilters, ...whereFiltersCopy },
      filterTags: { ...filterTags, ...tagsCopy },
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
    });
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(PAYMENT_STATUS_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'subscriptionPlanId':
          forEach(PLAN_NAMES_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt' || dataIndex === 'expiryDate') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent spinning={filterLoading} setHeight={35}>
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div className="filter-checkboxes">
              {filterList?.length > 0 ? (
                map(filterList, (item) => (
                  <div className="single-checkbox-div" key={item?.key}>
                    <Checkbox
                      value={item}
                      checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                      key={item?.key || item}
                      onChange={(e) => changeFilter(e, dataIndex)}
                      className="single-checkbox"
                    >
                      <span>{item?.label}</span>
                    </Checkbox>
                  </div>
                ))
              ) : (
                <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });
  const columns = [
    {
      title: 'User Name',
      dataIndex: 'user',
      key: 'user',
      fixed: 'left',
      width: 150,
      onCell: (record) => ({
        onClick: (e) =>
          isUserPermission &&
          record?.user?.id &&
          handleOpenUserDrawer(e, record?.user, dispatch),
        className: `${record?.user?.id && 'pointer table-cell'}`,
      }),
      render: (user) => (
        <>
          {user?.firstName || user?.lastName ? (
            <div className="table-data-with-id">{`${
              user?.firstName ? user?.firstName : ''
            } ${user?.lastName ? user?.lastName : ''}`}</div>
          ) : (
            '-'
          )}
          {user?.userNumber ? (
            <div className="font-size-12">({user?.userNumber})</div>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: 'User Phone Number',
      dataIndex: 'phoneNo',
      key: 'phoneNo',
      width: 200,
      render: (phoneNo) => phoneNo || '-',
    },
    {
      title: 'User Email',
      dataIndex: 'email',
      key: 'email',
      width: 150,
      render: (email) => email || '-',
    },
    {
      title: 'Plan Name',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },

    {
      title: 'Actual Price (₹)',
      dataIndex: 'actualPrice',
      key: 'actualPrice',
      width: 150,
      render: (actualPrice) => `₹${actualPrice}`,
    },
    {
      title: 'Selling Price (₹)',
      dataIndex: 'sellingPrice',
      key: 'sellingPrice',
      width: 150,
      render: (sellingPrice) => `₹${sellingPrice}`,
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 150,
      render: (paymentStatus) => (
        <Tag color={PAYOUT_LINK_STATUS?.[paymentStatus]?.color}>
          {paymentStatus}
        </Tag>
      ),
      ...filterPopup('status'),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 200,
      render: (createdBy) => (
        <>
          {createdBy?.firstName || createdBy?.lastName ? (
            <div className="table-data-with-id">{`${
              createdBy?.firstName ? createdBy?.firstName : ''
            } ${createdBy?.lastName ? createdBy?.lastName : ''}`}</div>
          ) : (
            '-'
          )}
          {createdBy?.userNumber ? (
            <div className="font-size-12">({createdBy?.userNumber})</div>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (date) => dayjs(date).format('DD/MM/YYYY hh:mm A'),
    },
    {
      title: 'Expires On',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      width: 200,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'expiredAt' && sortedInfo?.order,
      render: (date) => dayjs(date).format('DD/MM/YYYY hh:mm A'),
    },
    {
      title: 'Custom Plan Expiry Date',
      dataIndex: 'planExpiryDate',
      key: 'planExpiryDate',
      width: 200,
      render: (planExpiryDate) =>
        planExpiryDate
          ? dayjs(planExpiryDate).format('DD/MM/YYYY hh:mm A')
          : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="action-button">
          <Tooltip title="Copy Offer Link">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e?.stopPropagation();
                handleCopyLink(record?.paymentLink);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ]?.filter((item) => {
    if (isSalesUser(currentUser) && item?.dataIndex === 'createdBy') {
      return item?.hidden;
    }

    return !item?.hidden;
  });

  const handleTableChange = (pagination, filters, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setSortedInfo(sorter);
    saveRetainedFilterState(TABLE_FILTER_KEYS.PAYMENTS_PAYOUT_LINKS, {
      paginationProp: { ...paginationProp, ...pagination },
      sort: sorter,
    });

    paymentLinks({
      variables: {
        filter: {
          ...initialFilter,
          skip,
          limit: pagination?.pageSize,
          search: searchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          subscriptionPlanId:
            finalWhereFilters?.subscriptionPlanId?.length > 0
              ? finalWhereFilters?.subscriptionPlanId
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const locale = {
    emptyText: isEmptyList ? '' : <span />,
  };

  return (
    <>
      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={paymentLinksData}
        loading={loading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey="id"
      />
    </>
  );
};

export default UserPayoutLinksTable;
